import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, TrendingUp, Calendar, User, UserCheck } from "lucide-react";
import { OrganizationWithRelations } from "@/types/organization.types";

interface AdditionalDetailsCardProps {
  organization: OrganizationWithRelations;
  contacts?: any[];
}

export function AdditionalDetailsCard({ organization, contacts = [] }: AdditionalDetailsCardProps) {
  // Find primary and day-to-day contacts
  const primaryContact = contacts.find(c => c.id === organization.primary_contact_id);
  const dayToDayContact = contacts.find(c => c.id === organization.day_to_day_contact_id);
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Additional Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Hierarchy Information */}
        {(organization.hierarchy_level !== null || organization.parent_id) && (
          <div className="space-y-2">
            <span className="text-sm font-medium text-muted-foreground">Hierarchy:</span>
            <div className="grid grid-cols-1 gap-1">
              {organization.hierarchy_level !== null && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Level:</span>
                  <Badge variant="outline">{organization.hierarchy_level}</Badge>
                </div>
              )}
              {organization.parent_id && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Has Parent:</span>
                  <Badge variant="secondary">Yes</Badge>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Key Contacts */}
        {(primaryContact || dayToDayContact) && (
          <div className="border-t pt-4 space-y-2">
            <span className="text-sm font-medium text-muted-foreground">Key Contacts:</span>
            {primaryContact && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground flex items-center">
                  <User className="h-4 w-4 mr-1" /> Primary Contact:
                </span>
                <span className="text-sm font-medium">
                  {primaryContact.first_name} {primaryContact.last_name}
                </span>
              </div>
            )}
            {dayToDayContact && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground flex items-center">
                  <UserCheck className="h-4 w-4 mr-1" /> Day-to-Day Contact:
                </span>
                <span className="text-sm font-medium">
                  {dayToDayContact.first_name} {dayToDayContact.last_name}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Metadata */}
        {(organization.created_at || organization.updated_at) && (
          <div className="border-t pt-4 space-y-2">
            <span className="text-sm font-medium text-muted-foreground">Record Information:</span>
            {organization.created_at && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground flex items-center">
                  <Calendar className="h-4 w-4 mr-1" /> Created:
                </span>
                <span className="text-sm font-medium">
                  {new Date(organization.created_at).toLocaleDateString()}
                </span>
              </div>
            )}
            {organization.updated_at && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Last Updated:</span>
                <span className="text-sm font-medium">
                  {new Date(organization.updated_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Logo Display */}
        {organization.logo_url && (
          <div className="border-t pt-4">
            <span className="text-sm font-medium text-muted-foreground">Logo:</span>
            <div className="mt-2">
              <img 
                src={organization.logo_url} 
                alt={`${organization.name} logo`}
                className="h-12 w-auto object-contain rounded border"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          </div>
        )}

        {/* Hierarchy Path */}
        {organization.hierarchy_path && organization.hierarchy_path.length > 0 && (
          <div className="border-t pt-4">
            <span className="text-sm font-medium text-muted-foreground">Hierarchy Path:</span>
            <div className="mt-2 flex flex-wrap gap-1">
              {organization.hierarchy_path.map((path, index) => (
                <div key={index} className="flex items-center">
                  <Badge variant="outline" className="text-xs">{path}</Badge>
                  {index < organization.hierarchy_path!.length - 1 && (
                    <span className="mx-1 text-muted-foreground">→</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Show message if no additional details */}
        {!organization.hierarchy_level && 
         !organization.parent_id && 
         !primaryContact && 
         !dayToDayContact && 
         !organization.created_at && 
         !organization.logo_url && 
         (!organization.hierarchy_path || organization.hierarchy_path.length === 0) && (
          <div className="text-sm text-muted-foreground italic">
            No additional details available
          </div>
        )}
      </CardContent>
    </Card>
  );
}